import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

const iconTooltipBadgeVariants = cva('inline-flex items-center gap-1.5 shrink-0 cursor-pointer', {
  variants: {
    tone: {
      success: '',
      warning: '',
      info: '',
      destructive: '',
    },
    display: {
      circle: 'h-7 w-7 justify-center rounded-full',
      inline: 'italic',
    },
  },
  compoundVariants: [
    { tone: 'success', display: 'circle', class: 'bg-emerald-100 text-emerald-700' },
    { tone: 'warning', display: 'circle', class: 'bg-amber-100 text-amber-700' },
    { tone: 'info', display: 'circle', class: 'bg-blue-100 text-blue-700' },
    { tone: 'destructive', display: 'circle', class: 'bg-red-100 text-red-700' },
    { tone: 'success', display: 'inline', class: 'text-emerald-600' },
    { tone: 'warning', display: 'inline', class: 'text-amber-600' },
    { tone: 'info', display: 'inline', class: 'text-blue-600' },
    { tone: 'destructive', display: 'inline', class: 'text-red-600' },
  ],
  defaultVariants: {
    tone: 'info',
    display: 'circle',
  },
});

export interface IconTooltipBadgeProps extends VariantProps<typeof iconTooltipBadgeVariants> {
  icon: LucideIcon;
  /** Full message shown on hover/tap. Pass a translated string. */
  text: string;
  /** When provided, renders icon + label text inline instead of an icon-only circular badge. */
  label?: string;
  testId?: string;
}

/**
 * Small badge that reveals its full message via a Tooltip on hover or tap,
 * instead of an always-visible wide text pill. Controlled open state so the
 * trigger's onClick also toggles the tooltip for touch.
 *
 * Two display shapes, chosen automatically by whether `label` is passed:
 * - icon-only circle (default) — for compact corner badges (e.g. confidence indicators)
 * - icon + inline label text — for inline affordances embedded in table cells etc.
 */
export function IconTooltipBadge({ icon: Icon, text, tone, label, testId }: IconTooltipBadgeProps) {
  const [open, setOpen] = React.useState(false);
  const display = label !== undefined ? 'inline' : 'circle';

  function toggle() {
    setOpen((prev) => !prev);
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          {/* eslint-disable-next-line react/forbid-elements -- tooltip trigger, not a Button */}
          <span
            role="button"
            tabIndex={0}
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') toggle();
            }}
            className={cn(iconTooltipBadgeVariants({ tone, display }))}
            aria-label={text}
            data-testid={testId}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" data-testid={testId ? `${testId}-tooltip` : undefined}>
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
